import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, FileText, User, MapPin, 
  Calendar, Tag, Send, AlertTriangle, Info, Truck, Printer, Paperclip, Shield, CheckCircle, Clock, Download, Trash2, Eye,
  X, Plus
} from 'lucide-react';
import toast from 'react-hot-toast';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Combobox from '../components/ui/Combobox';
import Textarea from '../components/ui/Textarea';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Badge from '../components/ui/Badge';
import CaseHistory from '../components/CaseHistory';
import FilePreviewModal from '../components/FilePreviewModal';
import ContactCombobox from '../components/shared/ContactCombobox';
import QRCode from 'react-qr-code';
import { getStoredKeys, signData } from '../utils/crypto';

const DispatchForm = () => {
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const navigate = useNavigate();
  const { user } = useAuth();
  const { get, post, put, del } = useApi();
  const { settings } = useSettings();
  const [office, setOffice] = useState(null);

  const escapeRegex = (value) => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const buildNumberValidation = (format, officeCode) => {
    const fmt = String(format || '').trim();
    if (!fmt) return { example: '', regex: null };
    const officeValue = officeCode || '';
    const example = fmt
      .replaceAll('{OFFICE}', officeValue || 'OFFICE')
      .replaceAll('{YYYY}', String(new Date().getFullYear()))
      .replaceAll('{SEQ}', '0001');
    const tokens = fmt.split(/(\{OFFICE\}|\{YYYY\}|\{SEQ\})/g).filter(Boolean);
    const pattern = tokens.map((t) => {
      if (t === '{OFFICE}') return officeValue ? escapeRegex(officeValue) : '[A-Z0-9_-]+';
      if (t === '{YYYY}') return '\\d{4}';
      if (t === '{SEQ}') return '\\d{4}';
      return escapeRegex(t);
    }).join('');
    return { example, regex: new RegExp(`^${pattern}$`, 'i') };
  };

  const dispatchNumberFormat = settings?.dispatch_number_format || 'WAPDA/DISP/{YYYY}/{SEQ}';
  const dispatchNumberValidation = buildNumberValidation(dispatchNumberFormat, user?.officeCode);
  const numberFieldPattern = (dispatchNumberValidation.regex?.source || '').replace(/^\^/, '').replace(/\$$/, '');
  const sanitizeNumberField = (raw) => String(raw || '').replace(/[^A-Za-z0-9/_() .&-]/g, '');

  const [formData, setFormData] = useState({
    dispatchNumber: '',
    subject: '',
    recipient: '',
    recipientAddress: '',
    recipientDept: '',
    dateIssued: new Date().toISOString().split('T')[0],
    modeOfDispatch: 'Post',
    priority: 'Normal',
    status: 'Dispatched',
    category: 'General',
    remarks: '',
    deliveredTo: '',
    sendingOfficerName: '',
    sendingOfficerDesignation: '',
    sendingOffice: '',
    isConfidential: false
  });

  const canEdit = true;
  const draftKey = user?.userId ? `dispatchFormDraft:${user.userId}` : 'dispatchFormDraft';

  const clearDraft = () => {
    try {
      sessionStorage.removeItem(draftKey);
    } catch {
      void 0;
    }
  };

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [hasKeys, setHasKeys] = useState(false);
  const [shouldSign, setShouldSign] = useState(false);
  const [isAddressReadOnly, setIsAddressReadOnly] = useState(false);

  const [currentStep, setCurrentStep] = useState(0);
  const steps = [
    { key: 'details', label: 'Dispatch Details' },
    { key: 'recipient', label: 'Recipient Info' },
    { key: 'delivery', label: 'Delivery' },
    { key: 'attachments', label: 'Attachments' }
  ];
  const stepsLength = steps.length;
  
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    variant: 'danger',
    onConfirm: () => {}
  });

  useEffect(() => {
    if (isEdit) {
      fetchDispatch();
    }
    checkKeys();
  }, [id]);

  useEffect(() => {
    if (isEdit) return;
    if (!user?.userId) return;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return;
      if (parsed.formData && typeof parsed.formData === 'object') {
        setFormData(prev => ({ ...prev, ...parsed.formData }));
      }
      if (Number.isFinite(parsed.currentStep)) {
        setCurrentStep(Math.max(0, Math.min(Number(parsed.currentStep), stepsLength - 1)));
      }
      if (typeof parsed.isAddressReadOnly === 'boolean') {
        setIsAddressReadOnly(parsed.isAddressReadOnly);
      }
    } catch {
      void 0;
    }
  }, [isEdit, user?.userId, draftKey, stepsLength]);

  useEffect(() => {
    if (isEdit) return;
    if (!user?.userId) return;
    try {
      sessionStorage.setItem(draftKey, JSON.stringify({ formData, currentStep, isAddressReadOnly }));
    } catch {
      void 0;
    }
  }, [isEdit, user?.userId, formData, currentStep, isAddressReadOnly, draftKey]);

  useEffect(() => {
    const fetchOffice = async () => {
      if (!user?.officeId) return;
      try {
        const response = await get(`/offices/${user.officeId}`);
        setOffice(response.data);
      } catch (e) {
        setOffice(null);
      }
    };
    fetchOffice();
  }, [get, user?.officeId]);

  useEffect(() => {
    if (isEdit) return;
    setFormData(prev => ({
      ...prev,
      sendingOfficerName: prev.sendingOfficerName || user?.name || '',
      sendingOfficerDesignation: prev.sendingOfficerDesignation || user?.designation || user?.role || '',
      sendingOffice: prev.sendingOffice || office?.name || user?.officeName || ''
    }));
  }, [isEdit, office?.name, user?.designation, user?.name, user?.officeName, user?.role]);

  const checkKeys = async () => {
    const keys = await getStoredKeys();
    if (keys) {
      setHasKeys(true);
      setShouldSign(true);
    }
  };

  const fetchDispatch = async () => {
    try {
      const response = await get(`/dispatch/${id}`);
      setFormData({
        ...response.data,
        category: response.data.category || 'General',
        modeOfDispatch: response.data.modeOfDispatch || 'Post',
        priority: response.data.priority || 'Normal',
        status: response.data.status || 'Dispatched',
        isConfidential: response.data.isConfidential === 1 || response.data.isConfidential === true,
      });
      setExistingFiles(response.data.attachments || []);
    } catch (error) {
      toast.error('Failed to load dispatch details');
      navigate('/dispatch');
    }
  };


  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = (name === 'dispatchNumber' && !isEdit && settings.dispatch_number_scheme === 'manual')
      ? sanitizeNumberField(value)
      : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleDownloadBundle = async () => {
    try {
      const pdfFiles = existingFiles.filter(f => f.mimeType === 'application/pdf');
      if (pdfFiles.length === 0) return;

      const fileIds = pdfFiles.map(f => f.id);
      const bundleName = `Bundle_${formData.dispatchNumber || 'Dispatch'}.pdf`;

      const response = await post('/pdf/bundle', { fileIds, bundleName }, { responseType: 'blob' });

      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', bundleName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      toast.success('PDF Bundle downloaded');
    } catch (e) {
      console.error("Bundle failed", e);
      toast.error('Failed to download bundle');
    }
  };

  const handleDownload = async (file) => {
    try {
      const blob = await get(`/files/${file.fileName}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setIsPreviewOpen(true);
  };

  const handleDeleteFile = (fileId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete File',
      message: 'Are you sure you want to delete this file?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await del(`/files/${fileId}`);
          setExistingFiles(prev => prev.filter(f => f.id !== fileId));
          toast.success('File deleted successfully');
        } catch (error) {
          console.error('Delete error:', error);
          toast.error('Failed to delete file');
        }
      }
    });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.recipient.trim()) newErrors.recipient = 'Recipient is required';
    if (!formData.dateIssued) newErrors.dateIssued = 'Date Issued is required';
    
    // Check for manual dispatch number
    if (!isEdit && settings.dispatch_number_scheme === 'manual' && !formData.dispatchNumber?.trim()) {
      newErrors.dispatchNumber = 'Dispatch Number is required in manual mode';
    } else if (!isEdit && settings.dispatch_number_scheme === 'manual' && formData.dispatchNumber?.trim()) {
      if (dispatchNumberValidation.regex && !dispatchNumberValidation.regex.test(formData.dispatchNumber.trim())) {
        newErrors.dispatchNumber = `Invalid dispatch number format. Example: ${dispatchNumberValidation.example}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setConfirmDialog({
      isOpen: true,
      title: isEdit ? 'Update Dispatch' : 'Create Dispatch',
      message: `Are you sure you want to ${isEdit ? 'update' : 'create'} this dispatch record?`,
      confirmText: isEdit ? 'Update' : 'Create',
      variant: 'primary',
      onConfirm: submitForm
    });
  };

  const validateStep = (stepIndex) => {
    const newErrors = {};
    if (stepIndex === 0) {
      if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
      if (!formData.dateIssued) newErrors.dateIssued = 'Date Issued is required';
      if (!isEdit && settings.dispatch_number_scheme === 'manual' && !formData.dispatchNumber?.trim()) {
        newErrors.dispatchNumber = 'Dispatch Number is required in manual mode';
      } else if (!isEdit && settings.dispatch_number_scheme === 'manual' && formData.dispatchNumber?.trim()) {
        if (dispatchNumberValidation.regex && !dispatchNumberValidation.regex.test(formData.dispatchNumber.trim())) {
          newErrors.dispatchNumber = `Invalid dispatch number format. Example: ${dispatchNumberValidation.example}`;
        }
      }
    }
    if (stepIndex === 1) {
      if (!formData.recipient.trim()) newErrors.recipient = 'Recipient is required';
    }
    setErrors(prev => ({ ...prev, ...newErrors }));
    const ok = Object.keys(newErrors).length === 0;
    if (!ok) toast.error('Please complete required fields');
    return ok;
  };

  const goNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
    }
  };

  const goPrev = () => {
    setCurrentStep((s) => Math.max(s - 1, 0));
  };

  const submitForm = async () => {
    setLoading(true);
    try {
      const data = new FormData();
      const payload = { ...formData, approvalStatus: 'Approved' };
      Object.keys(payload).forEach(key => {
        // Handle null/undefined values
        if (payload[key] !== null && payload[key] !== undefined) {
          data.append(key, payload[key]);
        }
      });
      
      selectedFiles.forEach(file => {
        data.append('files', file);
      });

      // Add Digital Signature
      if (shouldSign && hasKeys) {
        try {
          const keys = await getStoredKeys();
          if (keys && keys.privateKey) {
            const payloadToSign = `${formData.subject}|${formData.recipient}|${formData.dateIssued}`;
            const signature = await signData(keys.privateKey, payloadToSign);
            data.append('digitalSignature', signature);
            toast.success('Document digitally signed');
          }
        } catch (signError) {
          console.error('Signing failed', signError);
          toast.error('Digital signing failed, proceeding without signature');
        }
      }

      if (isEdit) {
        await put(`/dispatch/${id}`, data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Dispatch updated successfully');
      } else {
        await post('/dispatch', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        toast.success('Dispatch created successfully');
      }
      if (!isEdit) clearDraft();
      navigate('/dispatch');
    } catch (error) {
      console.error('Failed to save dispatch', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save dispatch');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
     setConfirmDialog({
      isOpen: true,
      title: 'Cancel Changes',
      message: 'Are you sure you want to cancel? Unsaved changes will be lost.',
      confirmText: 'Yes, Cancel',
      variant: 'warning',
      onConfirm: () => {
        if (!isEdit) clearDraft();
        navigate('/dispatch');
      }
    });
  };

  return (
    <div className="max-w-5xl mx-auto mb-12 px-1 sm:px-0">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dispatch')}
            className="hover:bg-gray-100 rounded-full p-2 transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight truncate">
                {isEdit ? 'Edit Dispatch' : 'New Dispatch'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                {isEdit && <Badge variant="neutral">{formData.dispatchNumber}</Badge>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stepper */}
      <div className="mb-8 overflow-x-auto">
        <div className="min-w-[560px] flex items-center justify-between relative px-1">
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-1 bg-gray-200 -z-10" />
          {steps.map((step, index) => (
            <div 
              key={step.key}
              className={`flex flex-col items-center gap-2 bg-white px-2 cursor-pointer ${
                index <= currentStep ? 'text-indigo-600' : 'text-gray-400'
              }`}
              onClick={() => index < currentStep && setCurrentStep(index)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-colors ${
                index <= currentStep ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {index + 1}
              </div>
              <span className="text-xs font-medium whitespace-nowrap">{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 md:p-8">
          
          {/* Step 0: Dispatch Details */}
          {currentStep === 0 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-6">
                <FileText className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-800">Dispatch Details</h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2">
                  <Input
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    error={errors.subject}
                    required
                    placeholder="Enter dispatch subject"
                    className="text-lg"
                  />
                </div>

                <Input
                  type="date"
                  label="Date Issued"
                  name="dateIssued"
                  value={formData.dateIssued}
                  onChange={handleChange}
                  error={errors.dateIssued}
                  required
                />

                {!isEdit && settings.dispatch_number_scheme === 'manual' && (
                  <div>
                    <Input
                      label="Dispatch Number"
                      name="dispatchNumber"
                      value={formData.dispatchNumber}
                      onChange={handleChange}
                      onBlur={() => {
                        if (isEdit || settings.dispatch_number_scheme !== 'manual') return;
                        const v = String(formData.dispatchNumber || '').trim();
                        if (!v) return;
                        if (dispatchNumberValidation.regex && !dispatchNumberValidation.regex.test(v)) {
                          setErrors(prev => ({ ...prev, dispatchNumber: `Invalid dispatch number format. Example: ${dispatchNumberValidation.example}` }));
                        }
                      }}
                      error={errors.dispatchNumber}
                      required
                      placeholder={dispatchNumberValidation.example ? `e.g. ${dispatchNumberValidation.example}` : `Format: ${dispatchNumberFormat}`}
                      maxLength={80}
                      pattern={numberFieldPattern || undefined}
                      title={dispatchNumberValidation.example ? `Example: ${dispatchNumberValidation.example}` : undefined}
                    />
                    <p className="text-xs text-gray-500 mt-1">Format: {dispatchNumberFormat}</p>
                  </div>
                )}

                <Combobox
                  label="Category"
                  value={formData.category}
                  onChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                  options={[
                    { value: 'General', label: 'General' },
                    { value: 'Urgent', label: 'Urgent' },
                    { value: 'Confidential', label: 'Confidential' },
                    { value: 'Legal', label: 'Legal' },
                    { value: 'Financial', label: 'Financial' },
                    { value: 'Administrative', label: 'Administrative' }
                  ]}
                />

                <Combobox
                  label="Priority"
                  value={formData.priority}
                  onChange={(val) => setFormData(prev => ({ ...prev, priority: val }))}
                  options={[
                    { value: 'Normal', label: 'Normal' },
                    { value: 'Urgent', label: 'Urgent' },
                    { value: 'Immediate', label: 'Immediate' }
                  ]}
                />
                
                <div className="flex items-center h-full pt-6">
                  <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors w-full">
                    <input
                      type="checkbox"
                      name="isConfidential"
                      checked={formData.isConfidential}
                      onChange={(e) => setFormData(prev => ({ ...prev, isConfidential: e.target.checked }))}
                      className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300"
                    />
                    <div className="flex items-center gap-2">
                      <Shield className={`w-4 h-4 ${formData.isConfidential ? 'text-red-500' : 'text-gray-400'}`} />
                      <span className="text-gray-700 font-medium">Mark as Confidential</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Recipient Info */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-6">
                <User className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-800">Recipient Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 <div className="col-span-2">
                  <ContactCombobox
                    label="Recipient Name"
                    value={formData.recipient}
                    onSelect={(contact) => {
                      const name = contact.contactPerson || contact.organizationName || contact.email;
                      setFormData(prev => ({
                        ...prev,
                        recipient: name,
                        recipientAddress: contact.address || prev.recipientAddress
                      }));
                      setIsAddressReadOnly(!!contact.address);
                      if (errors.recipient) setErrors(prev => ({ ...prev, recipient: null }));
                    }}
                    error={errors.recipient}
                    required
                    placeholder="Search or enter recipient name"
                  />
                </div>

                <div className="col-span-2">
                  <Textarea
                    label="Recipient Address"
                    name="recipientAddress"
                    value={formData.recipientAddress}
                    onChange={handleChange}
                    placeholder="Enter full address"
                    rows={3}
                    readOnly={isAddressReadOnly}
                    className={isAddressReadOnly ? 'bg-gray-50' : ''}
                  />
                  {isAddressReadOnly && (
                    <button 
                      type="button" 
                      onClick={() => setIsAddressReadOnly(false)}
                      className="text-xs text-indigo-600 hover:text-indigo-800 mt-1"
                    >
                      Edit Address
                    </button>
                  )}
                </div>

                <Input
                  label="Recipient Department"
                  name="recipientDept"
                  value={formData.recipientDept}
                  onChange={handleChange}
                  placeholder="Department/Section"
                />

                <div className="col-span-2 pt-4 border-t border-gray-100 mt-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-4 uppercase tracking-wider">Sender Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Input
                      label="Sending Officer"
                      name="sendingOfficerName"
                      value={formData.sendingOfficerName}
                      onChange={handleChange}
                      placeholder="Officer Name"
                    />
                    <Input
                      label="Designation"
                      name="sendingOfficerDesignation"
                      value={formData.sendingOfficerDesignation}
                      onChange={handleChange}
                      placeholder="Designation"
                    />
                    <Input
                      label="Office"
                      name="sendingOffice"
                      value={formData.sendingOffice}
                      onChange={handleChange}
                      placeholder="Office Name"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Delivery */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-6">
                <Truck className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-semibold text-gray-800">Delivery</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Combobox
                  label="Mode of Dispatch"
                  value={formData.modeOfDispatch}
                  onChange={(val) => setFormData(prev => ({ ...prev, modeOfDispatch: val }))}
                  options={((settings.dispatch_modes || []).length > 0 ? settings.dispatch_modes : ['Post', 'Courier', 'Hand Delivery', 'Email', 'Fax', 'Special Messenger']).map(m => ({ value: m, label: m }))}
                />

                <Input
                  label="Delivered To (if applicable)"
                  name="deliveredTo"
                  value={formData.deliveredTo}
                  onChange={handleChange}
                  placeholder="Name of receiver"
                />
              </div>
            </div>
          )}

          {/* Step 3: Attachments */}
          {currentStep === 3 && (
             <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
              
              {/* Attachments Section */}
              <div>
                <div className="flex items-center justify-between pb-4 border-b border-gray-100 mb-6">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-5 h-5 text-indigo-600" />
                    <h2 className="text-lg font-semibold text-gray-800">Attachments</h2>
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Button variant="outline" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Files
                    </Button>
                  </div>
                </div>

                {(existingFiles.length > 0 || selectedFiles.length > 0) ? (
                  <div className="space-y-3 mb-6">
                    {existingFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-gray-100 rounded-lg">
                            <FileText className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.originalName}</p>
                            <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(file)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(file)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteFile(file.id)} className="text-red-500 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    {selectedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <FileText className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-blue-900">{file.name}</p>
                            <p className="text-xs text-blue-600">New Upload</p>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))}
                          className="text-red-500 hover:bg-red-50"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                   <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300 mb-6">
                    No files attached
                  </div>
                )}
              </div>

              {/* QR Code & Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2 bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">Summary</h3>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-4 text-sm">
                    <div>
                      <dt className="text-gray-500">Subject</dt>
                      <dd className="font-medium text-gray-900">{formData.subject || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Recipient</dt>
                      <dd className="font-medium text-gray-900">{formData.recipient || '-'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Date Issued</dt>
                      <dd className="font-medium text-gray-900">{formData.dateIssued}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Mode</dt>
                      <dd className="font-medium text-gray-900">{formData.modeOfDispatch}</dd>
                    </div>
                  </dl>
                </div>
                
                <div className="flex flex-col items-center justify-center p-6 bg-white rounded-lg border border-gray-200 shadow-sm">
                  {formData.dispatchNumber ? (
                    <>
                      <QRCode value={formData.dispatchNumber} size={120} />
                      <p className="mt-3 text-xs text-gray-500 font-mono">{formData.dispatchNumber}</p>
                    </>
                  ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded flex items-center justify-center text-gray-400 text-xs text-center p-2">
                      QR Code generated after save
                    </div>
                  )}
                </div>
              </div>

              {/* Digital Signature */}
              {hasKeys && (
                <div className="bg-indigo-50 p-6 rounded-xl border border-indigo-100 mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <Shield className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-indigo-900 uppercase tracking-wider">Digital Signature</h3>
                      <p className="text-sm text-indigo-700 mt-1">Sign this dispatch with your digital key for authenticity.</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={shouldSign}
                      onChange={(e) => setShouldSign(e.target.checked)}
                      className="sr-only peer" 
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              )}

            </div>
          )}

        </div>

        {/* Footer Navigation */}
        <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            variant="ghost"
            onClick={goPrev}
            disabled={currentStep === 0}
            className="w-full sm:w-auto"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep < steps.length - 1 ? (
            <Button
              variant="primary"
              onClick={goNext}
              className="w-full sm:w-auto"
            >
              Next Step
              <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleSubmit}
              isLoading={loading}
              disabled={!canEdit}
              className="w-full sm:w-auto"
            >
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Update Dispatch' : 'Create Dispatch'}
            </Button>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText={confirmDialog.confirmText}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog(prev => ({ ...prev, isOpen: false }));
        }}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        variant={confirmDialog.variant}
      />

      {isPreviewOpen && previewFile && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
        />
      )}
    </div>
  );
};

export default DispatchForm;
